import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectListItem } from '@recotex/shared';
import { AppShell, Icon } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { BouwflowLink } from '../components/BouwflowLink';
import { TweePercentages } from '../components/TweePercentages';
import { TagChips } from '../components/Tags';
import { api } from '../lib/api';
import { fmtEUR, fmtDate, fmtDateTime, statusChipClass, statusLabel } from '../lib/format';

type SortBy = 'createdAt' | 'totaal' | 'klantNaam' | 'projectNummer';
type SortDir = 'asc' | 'desc';
type JaNee = '' | 'ja' | 'nee';

export function Dashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [projectNummer, setProjectNummer] = useState('');
  const [ploegId, setPloegId] = useState('');
  const [status, setStatus] = useState<'' | 'draft' | 'matched' | 'approved'>('');
  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minTotaal, setMinTotaal] = useState('');
  const [maxTotaal, setMaxTotaal] = useState('');
  // Filters 27okt: labels (6), verstuurd, open posten (4), gefactureerd door de OA, projectleider (3).
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [verstuurd, setVerstuurd] = useState<JaNee>('');
  const [onvolledig, setOnvolledig] = useState<JaNee>('');
  const [oaGefactureerd, setOaGefactureerd] = useState<JaNee>('');
  const [projectleider, setProjectleider] = useState('');
  // Blok C: oplevering en eindfactuur uit Bouwflow. ?zonderEindfactuur=ja komt uit de dagelijkse mail (8.2.1).
  const [zoekParams] = useSearchParams();
  const [opgeleverd, setOpgeleverd] = useState<JaNee>('');
  const [eindfactuur, setEindfactuur] = useState<'' | 'betaald' | 'verstuurd' | 'geen'>('');
  const [zonderEindfactuur, setZonderEindfactuur] = useState(zoekParams.get('zonderEindfactuur') === 'ja');
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [advancedOpen, setAdvancedOpen] = useState(zoekParams.get('zonderEindfactuur') === 'ja');

  const projectsQ = useQuery({
    queryKey: ['projects', { search, projectNummer, ploegId, status, projectTypes, dateFrom, dateTo, minTotaal, maxTotaal, tagIds, verstuurd, onvolledig, oaGefactureerd, projectleider, opgeleverd, eindfactuur, zonderEindfactuur, sortBy, sortDir }],
    queryFn: () => api.projects.list({
      search: search || undefined,
      projectNummer: projectNummer || undefined,
      ploegId: ploegId || undefined,
      status: status || undefined,
      projectTypes: projectTypes.length ? projectTypes.join(',') : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      minTotaal: minTotaal ? parseFloat(minTotaal) : undefined,
      maxTotaal: maxTotaal ? parseFloat(maxTotaal) : undefined,
      tagIds: tagIds.length ? tagIds.join(',') : undefined,
      verstuurd: verstuurd || undefined,
      onvolledig: onvolledig || undefined,
      oaGefactureerd: oaGefactureerd || undefined,
      projectleider: projectleider.trim() || undefined,
      opgeleverd: opgeleverd || undefined,
      eindfactuur: eindfactuur || undefined,
      zonderEindfactuur: zonderEindfactuur ? 'ja' : undefined,
      sortBy,
      sortDir,
      limit: 200,
    }),
  });
  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const typesQ = useQuery({ queryKey: ['project-types'], queryFn: () => api.projects.types() });
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: () => api.dossier.tags() });

  const projects = projectsQ.data?.projects ?? [];
  const ploegen = ploegenQ.data?.ploegen ?? [];
  const availableTypes = typesQ.data?.types ?? [];
  // Meest gebruikte tags eerst; tags die nergens op staan zijn geen nuttige filter.
  const filterTags = [...(tagsQ.data?.tags ?? [])]
    .filter((t) => t.gebruik > 0 || tagIds.includes(t.id))
    .sort((a, b) => b.gebruik - a.gebruik || a.naam.localeCompare(b.naam, 'nl'));

  const toggleProjectType = (t: string) =>
    setProjectTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const toggleTag = (id: string) =>
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSort = (col: SortBy) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'totaal' || col === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const resetFilters = () => {
    setSearch(''); setProjectNummer(''); setPloegId(''); setStatus('');
    setProjectTypes([]);
    setDateFrom(''); setDateTo('');
    setMinTotaal(''); setMaxTotaal('');
    setTagIds([]); setVerstuurd(''); setOnvolledig(''); setOaGefactureerd(''); setProjectleider('');
    setOpgeleverd(''); setEindfactuur(''); setZonderEindfactuur(false);
    setSortBy('createdAt'); setSortDir('desc');
  };

  const advancedActief = !!(dateFrom || dateTo || minTotaal || maxTotaal || tagIds.length || verstuurd || onvolledig || oaGefactureerd || projectleider.trim() || opgeleverd || eindfactuur || zonderEindfactuur);
  const hasActiveFilters = !!(search || projectNummer || ploegId || status || projectTypes.length) || advancedActief;

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Projecten</h1>
          <p className="page-subtitle">{projectsQ.data?.total ?? 0} project{projectsQ.data?.total === 1 ? '' : 'en'}</p>
        </div>
        <Link to="/project/new" className="btn-accent">+ Nieuw project</Link>
      </div>

      {/* Rij 1: status als pillen, weergave als segmented control. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status">
          {STATUS_PILLEN.map((s) => (
            <button key={s.waarde} type="button" className="pill" aria-pressed={status === s.waarde} onClick={() => setStatus(s.waarde)}>
              {s.dot && <span className={`pill-dot ${status === s.waarde ? 'ring-2 ring-white/70' : ''} ${s.dot}`} aria-hidden="true" />}
              {s.label}
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Weergave">
          <button type="button" className="segmented-item" aria-pressed={view === 'cards'} onClick={() => setView('cards')}>Kaarten</button>
          <button type="button" className="segmented-item" aria-pressed={view === 'table'} onClick={() => setView('table')}>Tabel</button>
        </div>
      </div>

      {/* Rij 2: zoeken en de vaste filters. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Icon name="zoek" size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-secondary-light" />
          <input
            type="search"
            placeholder="Zoek op klant, projectnr, of bestandsnaam…"
            aria-label="Zoek op klant, projectnummer of bestandsnaam"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-[36px]"
          />
        </div>
        <input
          type="search"
          placeholder="P-nummer…"
          aria-label="Zoek op P-nummer"
          title="Zoek op projectnummer (bv. P-1234 of 1234)"
          value={projectNummer}
          onChange={(e) => setProjectNummer(e.target.value)}
          className="input w-[140px]"
        />
        <select className="select w-auto min-w-[200px]" value={ploegId} onChange={(e) => setPloegId(e.target.value)} aria-label="Onderaannemer">
          <option value="">Alle onderaannemers</option>
          {ploegen.map((p) => (
            <option key={p.id} value={p.id}>{p.team} — {p.firma}</option>
          ))}
        </select>
        <button
          type="button"
          className={`btn-ghost ${advancedOpen ? 'bg-primary-50 border-border-strong' : ''}`}
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          Filters
          {advancedActief && <span className="pill-dot bg-accent" title="Er staan extra filters aan"><span className="sr-only">(actief)</span></span>}
          <Icon name="chevron" size={16} strokeWidth={2} className={`text-secondary transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
        </button>
        {hasActiveFilters && (
          <button type="button" className="px-2 py-2 text-sm font-medium text-secondary hover:text-primary" onClick={resetFilters}>Wissen</button>
        )}
      </div>

      {availableTypes.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <span className="text-xs font-semibold text-secondary mr-1">Type werken</span>
          {availableTypes.map(({ type, count }) => (
            <FilterPil key={type} actief={projectTypes.includes(type)} onClick={() => toggleProjectType(type)} label={type} aantal={count} />
          ))}
          {projectTypes.length > 0 && (
            <button type="button" className="text-xs font-medium text-secondary hover:text-primary underline ml-1" onClick={() => setProjectTypes([])}>
              wissen
            </button>
          )}
        </div>
      )}

      {advancedOpen && (
        <div className="card-flat mb-3 space-y-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label htmlFor="filter-datum-vanaf" className="field-label">Datum vanaf</label>
              <input id="filter-datum-vanaf" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input w-auto" />
            </div>
            <div>
              <label htmlFor="filter-datum-tot" className="field-label">tot</label>
              <input id="filter-datum-tot" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input w-auto" />
            </div>
            <div>
              <label htmlFor="filter-totaal-vanaf" className="field-label">Totaal vanaf €</label>
              <input id="filter-totaal-vanaf" type="number" min="0" step="0.01" placeholder="0" value={minTotaal} onChange={(e) => setMinTotaal(e.target.value)} className="input w-28 text-right tabular-nums" />
            </div>
            <div>
              <label htmlFor="filter-totaal-tot" className="field-label">tot €</label>
              <input id="filter-totaal-tot" type="number" min="0" step="0.01" placeholder="∞" value={maxTotaal} onChange={(e) => setMaxTotaal(e.target.value)} className="input w-28 text-right tabular-nums" />
            </div>
            <div>
              <label htmlFor="filter-sorteer" className="field-label">Sorteer op</label>
              <select id="filter-sorteer" className="select w-auto" value={`${sortBy}:${sortDir}`} onChange={(e) => {
                const [b, d] = e.target.value.split(':');
                setSortBy(b as SortBy);
                setSortDir(d as SortDir);
              }}>
                <option value="createdAt:desc">Datum (nieuwste eerst)</option>
                <option value="createdAt:asc">Datum (oudste eerst)</option>
                <option value="totaal:desc">Totaal (hoog → laag)</option>
                <option value="totaal:asc">Totaal (laag → hoog)</option>
                <option value="klantNaam:asc">Klant A → Z</option>
                <option value="klantNaam:desc">Klant Z → A</option>
                <option value="projectNummer:asc">Projectnr ↑</option>
                <option value="projectNummer:desc">Projectnr ↓</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <JaNeeFilter id="filter-verstuurd" label="Verstuurd" waarde={verstuurd} onChange={setVerstuurd} ja="Verstuurd" nee="Nog niet verstuurd"
              hint="Huidige versie naar de onderaannemer verstuurd" />
            <JaNeeFilter id="filter-open-posten" label="Open posten" waarde={onvolledig} onChange={setOnvolledig} ja="Met open posten" nee="Zonder open posten"
              hint="Posten open voor oplevering (nog niet factureerbaar)" />
            <JaNeeFilter id="filter-oa-gefactureerd" label="Gefactureerd door OA" waarde={oaGefactureerd} onChange={setOaGefactureerd} ja="Gefactureerd" nee="Nog niet gefactureerd"
              hint="De onderaannemer heeft deze calculatie gefactureerd" />
            <JaNeeFilter id="filter-opgeleverd" label="Opgeleverd" waarde={opgeleverd} onChange={setOpgeleverd} ja="Opgeleverd" nee="Niet opgeleverd"
              hint="Opleveringsbon door de klant ondertekend, of eindfactuur betaald zonder bon (Bouwflow)" />
            <div className="min-w-[170px]">
              <label htmlFor="filter-eindfactuur" className="field-label">Eindfactuur klant</label>
              <select id="filter-eindfactuur" className="select" value={eindfactuur} onChange={(e) => setEindfactuur(e.target.value as typeof eindfactuur)}>
                <option value="">Alle</option>
                <option value="betaald">Betaald</option>
                <option value="verstuurd">Verstuurd, niet betaald</option>
                <option value="geen">Nog geen eindfactuur</option>
              </select>
            </div>
            <div>
              <label htmlFor="filter-projectleider" className="field-label">Projectleider</label>
              <input
                id="filter-projectleider"
                type="search"
                placeholder="Naam…"
                value={projectleider}
                onChange={(e) => setProjectleider(e.target.value)}
                className="input w-44"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-secondary mr-1">Controle</span>
            <FilterPil actief={zonderEindfactuur} onClick={() => setZonderEindfactuur((v) => !v)} label="Verstuurd, geen eindfactuur"
              title="Verstuurd naar de onderaannemer, maar nog geen eindfactuur naar de klant (SPEC_workflow 8.2.1)" />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-secondary mr-1">Labels</span>
            {tagsQ.isLoading ? (
              <span className="text-xs text-secondary">Laden…</span>
            ) : filterTags.length === 0 ? (
              <span className="text-xs text-secondary">Nog geen labels op projecten.</span>
            ) : (
              filterTags.map((t) => (
                <FilterPil key={t.id} actief={tagIds.includes(t.id)} onClick={() => toggleTag(t.id)} label={t.naam} aantal={t.gebruik}
                  title={t.automatisch ? 'Automatisch label' : undefined} />
              ))
            )}
            {tagIds.length > 0 && (
              <button type="button" className="text-xs font-medium text-secondary hover:text-primary underline ml-1" onClick={() => setTagIds([])}>
                wissen
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-5">
      {projectsQ.isLoading ? (
        <p className="text-secondary">Laden…</p>
      ) : projects.length === 0 ? (
        <EmptyState
          title="Geen projecten gevonden"
          description={hasActiveFilters
            ? 'De huidige filters geven geen resultaten. Pas ze aan of begin opnieuw.'
            : 'Upload je eerste opstartfiche-PDF en zie de calculatie automatisch verschijnen.'}
          action={hasActiveFilters
            ? <button className="btn-ghost" onClick={resetFilters}>Filters wissen</button>
            : <Link to="/project/new" className="btn-accent">+ Nieuw project</Link>}
        />
      ) : view === 'cards' ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {projects.map((p) => (
            <div key={p.id} className="card relative flex flex-col transition-[border-color,box-shadow] hover:border-border-strong hover:shadow-md">
              {/* Stretched link: de hele kaart opent de calculatie. Elementen met een eigen klik of
                  tooltip (Bouwflow-link, percentages, open posten, labels) liggen erboven (z-[2]). */}
              <Link
                to={`/project/${p.id}`}
                className="absolute inset-0 z-[1] rounded-lg"
                aria-label={`Open calculatie ${p.klantNaam || 'project'}`}
              />
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  {p.projectNummer && (
                    <BouwflowLink bouwflowProjectId={p.bouwflowProjectId} className="relative z-[2] text-xs font-semibold text-accent-700 tabular-nums">
                      {p.projectNummer}
                    </BouwflowLink>
                  )}
                  <div className="text-base font-semibold truncate">{p.klantNaam || 'Onbekend'}</div>
                  <div className="text-xs text-secondary truncate">{p.klantAdres}</div>
                  {p.projectleider && <div className="text-xs text-secondary truncate">Projectleider {p.projectleider}</div>}
                </div>
                <span className={`${statusChipClass(p.status)} shrink-0`}>{statusLabel(p.status)}</span>
              </div>
              <VerzendStatus p={p} className="mt-2" />
              <div className="flex items-baseline gap-2 flex-wrap mt-3">
                <span className="text-xl font-bold text-primary tabular-nums">{fmtEUR(p.totaal)}</span>
                <OpenIndicator bedrag={p.openBedrag} className="relative z-[2]" />
              </div>
              <div className="relative z-[2] self-start max-w-full mt-1 mb-3">
                <TweePercentages data={p.percentages} compact />
              </div>
              {p.tags.length > 0 && (
                <div className="relative z-[2] self-start max-w-full mb-3">
                  <TagChips tags={p.tags} klein />
                </div>
              )}
              <div className="mt-auto flex justify-between items-center pt-3 border-t border-lightgrey gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="chip truncate">{p.ploegTeam} · {p.ploegFirma}</span>
                  {p.projectType && <span className="chip-neutral">{p.projectType}</span>}
                </div>
                <span className="text-xs text-secondary whitespace-nowrap tabular-nums">{fmtDate(p.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-flat p-0 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="thead-row">
                <SortHeader label="Projectnr" col="projectNummer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Klant" col="klantNaam" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-3">Onderaannemer</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Percentages</th>
                <SortHeader label="Datum" col="createdAt" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHeader label="Totaal" col="totaal" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-lightgrey last:border-b-0 hover:bg-primary-50/60 cursor-pointer align-top" onClick={() => navigate(`/project/${p.id}`)}>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    {p.projectNummer ? (
                      <BouwflowLink bouwflowProjectId={p.bouwflowProjectId} className="font-semibold text-accent-700 tabular-nums">
                        {p.projectNummer}
                      </BouwflowLink>
                    ) : <span className="text-secondary">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="max-w-[210px]">
                      <div className="font-semibold text-sm truncate" title={p.klantNaam}>{p.klantNaam || 'Onbekend'}</div>
                      <div className="text-xs text-secondary truncate" title={p.klantAdres}>{p.klantAdres}</div>
                      {p.projectleider && <div className="text-xs text-secondary truncate">Projectleider {p.projectleider}</div>}
                      {p.tags.length > 0 && <div className="mt-1"><TagChips tags={p.tags} klein /></div>}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-sm">{p.ploegTeam}</div>
                    <div className="text-xs text-secondary">{p.ploegFirma}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={statusChipClass(p.status)}>{statusLabel(p.status)}</span>
                    <VerzendStatus p={p} className="mt-1" />
                  </td>
                  <td className="px-3 py-3 text-sm min-w-[150px]"><TweePercentages data={p.percentages} compact /></td>
                  <td className="px-3 py-3 text-right text-sm tabular-nums whitespace-nowrap text-secondary">{fmtDate(p.createdAt)}</td>
                  <td className="px-3 py-3 text-right text-sm tabular-nums whitespace-nowrap">
                    <div className="font-bold">{fmtEUR(p.totaal)}</div>
                    <OpenIndicator bedrag={p.openBedrag} className="mt-1" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </AppShell>
  );
}

// Status als pillen met een gekleurde stip in de tint van de status-chip (zie statusChipClass).
const STATUS_PILLEN: Array<{ waarde: '' | 'draft' | 'matched' | 'approved'; label: string; dot?: string }> = [
  { waarde: '', label: 'Alle' },
  { waarde: 'draft', label: 'Concept', dot: 'bg-secondary-light' },
  { waarde: 'matched', label: 'Gematcht', dot: 'bg-accent' },
  { waarde: 'approved', label: 'Goedgekeurd', dot: 'bg-success' },
];

/** Pil om een filterwaarde aan of uit te zetten (type werken, labels). */
function FilterPil({ actief, onClick, label, aantal, title }: { actief: boolean; onClick: () => void; label: string; aantal?: number; title?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={actief} title={title} className="pill">
      {actief && <Icon name="check" size={14} strokeWidth={2.5} className="-ml-0.5" />}
      {label}
      {aantal != null && <span className={`pill-count ${actief ? 'text-white/80' : 'text-secondary'}`}>{aantal}</span>}
    </button>
  );
}

function JaNeeFilter({ id, label, waarde, onChange, ja, nee, hint }: {
  id: string; label: string; waarde: JaNee; onChange: (v: JaNee) => void; ja: string; nee: string; hint?: string;
}) {
  return (
    <div className="min-w-[170px]">
      <label htmlFor={id} className="field-label" title={hint}>{label}</label>
      <select id={id} className="select" value={waarde} onChange={(e) => onChange(e.target.value as JaNee)} title={hint}>
        <option value="">Alle</option>
        <option value="ja">{ja}</option>
        <option value="nee">{nee}</option>
      </select>
    </div>
  );
}

/** Verstuurd (huidige versie naar de OA), versienummer vanaf v2, en gefactureerd door de OA. */
function VerzendStatus({ p, className = '' }: { p: ProjectListItem; className?: string }) {
  const verstuurd = p.verstuurdeVersie != null ? p.verstuurdeVersie === p.versie : !!p.verstuurdOp;
  const versieTitel = p.verstuurdeVersie != null && p.verstuurdeVersie !== p.versie
    ? `Versie ${p.versie}; versie ${p.verstuurdeVersie} werd verstuurd, deze nog niet`
    : `Versie ${p.versie}`;
  const w = p.workflow;
  // 8.2.1: verstuurd naar de onderaannemer, nog geen eindfactuur naar de klant.
  const zonderEind = !!p.verstuurdOp && w?.eindfactuur === 'geen';
  const correctie = p.soort === 'correctie';
  const pct = p.factuurPercentage ?? null;
  if (!verstuurd && p.versie <= 1 && !p.oaGefactureerdOp && !w?.opgeleverd && !zonderEind && !correctie && pct == null) return null;
  return (
    <span className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {correctie && <span className="chip-warning" title="Correctie nadat de onderaannemer al betaald werd: verrekend op een volgende calculatie (SPEC_workflow 6.5)">correctie</span>}
      {pct != null && <span className="chip-warning tabular-nums" title="De onderaannemer mag nu dit percentage factureren, de rest na oplevering (SPEC_workflow 6.3)">nu {pct} %</span>}
      {w?.opgeleverd && (
        <span className="chip-success" title={w.opgeleverdVia === 'eindfactuur' ? 'Eindfactuur betaald zonder opleveringsbon' : `Opleveringsbon door de klant ondertekend op ${fmtDate(w.oplevering?.getekendOp)}`}>
          opgeleverd
        </span>
      )}
      {zonderEind && <span className="chip-warning" title="Verstuurd naar de onderaannemer, nog geen eindfactuur naar de klant">geen eindfactuur</span>}
      {verstuurd && (
        <span className="chip" title={p.verstuurdOp ? `Verstuurd naar de onderaannemer op ${fmtDateTime(p.verstuurdOp)}` : 'Verstuurd naar de onderaannemer'}>
          verstuurd
        </span>
      )}
      {p.versie > 1 && <span className="text-xs font-semibold text-secondary tabular-nums" title={versieTitel}>v{p.versie}</span>}
      {p.oaGefactureerdOp && (
        <span className="chip-success" title={`Gefactureerd door de onderaannemer op ${fmtDate(p.oaGefactureerdOp)}`}>OA gefactureerd</span>
      )}
    </span>
  );
}

/** Posten open voor oplevering (27okt 4): zachte oranje chip met het bedrag. */
function OpenIndicator({ bedrag, className = '' }: { bedrag: number; className?: string }) {
  if (!(bedrag > 0)) return null;
  return (
    <span
      className={`chip-warning tabular-nums ${className}`}
      title="Posten open voor oplevering: vastgelegde kost, de onderaannemer mag ze nog niet factureren"
    >
      open {fmtEUR(bedrag)}
    </span>
  );
}

interface SortHeaderProps {
  label: string;
  col: SortBy;
  sortBy: SortBy;
  sortDir: SortDir;
  onSort: (col: SortBy) => void;
  align?: 'left' | 'right';
}

function SortHeader({ label, col, sortBy, sortDir, onSort, align = 'left' }: SortHeaderProps) {
  const isActive = sortBy === col;
  return (
    <th
      className={`px-3 py-3 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:text-primary ${isActive ? 'text-primary' : ''}`}
      onClick={() => onSort(col)}
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {isActive && <Icon name="chevron" size={14} strokeWidth={2.25} className={sortDir === 'asc' ? 'rotate-180' : ''} />}
      </span>
    </th>
  );
}
